import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, RefreshCw, Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { cardImage } from "@/lib/img";
import { PLATFORMS, TONES } from "@/lib/share";
import PlatformPost from "@/components/PlatformPost";

function Skeleton() {
  return (
    <div className="space-y-3" data-testid="publisher-loading">
      <p className="text-sm text-neutral-500 flex items-center gap-2"><Sparkles className="w-4 h-4 text-brand animate-pulse" /> Creating your posts &amp; creatives…</p>
      {[0, 1, 2].map((i) => <div key={i} className="h-40 rounded-xl bg-neutral-100 animate-pulse" />)}
    </div>
  );
}

export function PublisherBody({ kind, keyId, data: initial }) {
  const [tone, setTone] = useState(initial?.tone || "engaging");
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const qk = ["social", kind, keyId, tone];
  const hasInitial = initial && tone === initial.tone && !initial.draft;
  const quick = useQuery({
    queryKey: [...qk, "instant"],
    queryFn: () => api.get("/social/captions", { params: { kind, key: keyId, tone, instant: true } }).then((r) => r.data),
    enabled: !hasInitial, staleTime: Infinity, retry: 1,
  });
  const ai = useQuery({
    queryKey: qk,
    queryFn: () => api.get("/social/captions", { params: { kind, key: keyId, tone } }).then((r) => r.data),
    enabled: !hasInitial && !!quick.data?.draft, staleTime: Infinity, retry: false,
  });
  const data = hasInitial ? initial : (ai.data || quick.data);
  const writing = !!quick.data?.draft && ai.isFetching;
  const isLoading = !data && quick.isLoading;
  const isError = !data && quick.isError;
  const error = quick.error;

  const regenerate = async () => {
    setBusy(true);
    try {
      const r = await api.get("/social/captions", { params: { kind, key: keyId, tone, regenerate: true } });
      qc.setQueryData(qk, r.data); toast.success("Fresh captions ready");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't regenerate"); }
    setBusy(false);
  };
  const copyLink = () => {
    navigator.clipboard?.writeText(data?.links?.facebook?.replace(/([?&])ref=facebook/, "$1ref=copy") || window.location.href).catch(() => {});
    toast.success("Link copied");
  };

  const item = data?.item;
  return (
    <div>
      {item && (
        <div className="flex gap-4 items-start" data-testid="publisher-item">
          {item.image && <img src={cardImage(item.image, 200)} alt="" className="w-20 h-20 rounded-lg object-cover border border-neutral-200 bg-white" />}
          <div className="min-w-0">
            <p className="font-semibold text-ink-950 leading-snug line-clamp-2">{item.title}</p>
            {item.subtitle && <p className="text-xs text-neutral-500 mt-0.5">{item.subtitle}</p>}
            {(item.price || item.discount) && <p className="text-sm font-bold text-emerald-600 mt-1">{item.price || item.discount}{item.price && item.discount ? ` · ${item.discount}` : ""}</p>}
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-4" data-testid="publisher-tones">
        {TONES.map((t) => (
          <button key={t.id} type="button" onClick={() => setTone(t.id)} data-testid={`tone-${t.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${tone === t.id ? "bg-ink-950 text-white" : "bg-neutral-100 text-ink-800 hover:bg-neutral-200"}`}>{t.label}</button>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3 mt-5 mb-4">
        <p className="text-xs text-neutral-500 flex items-center gap-1.5" data-testid="publisher-status">
          {writing ? "Quick captions below are ready to use now" : data ? "Captions ready — copy, download the card, and publish!" : " "}
        </p>
        <div className="flex gap-2">
          <button type="button" onClick={copyLink} data-testid="publisher-copy-link" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-neutral-200 text-sm font-semibold hover:bg-neutral-50"><Link2 className="w-4 h-4" /> Link</button>
          <button type="button" onClick={regenerate} disabled={busy || !data} data-testid="publisher-regenerate" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-neutral-200 text-sm font-semibold hover:bg-neutral-50 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Regenerate
          </button>
        </div>
      </div>
      {(writing || busy) && data && (
        <div className="mb-4 rounded-xl border border-brand/20 bg-brand/5 px-4 py-3" data-testid="publisher-creating">
          <p className="text-sm font-semibold text-ink-950 flex items-center gap-2"><Sparkles className="w-4 h-4 text-brand animate-pulse" /> Creating your AI posts &amp; creatives for 8 platforms…</p>
          <p className="text-xs text-neutral-500 mt-0.5">This takes a few seconds. They'll appear here automatically.</p>
          <div className="mt-2 h-1 rounded-full bg-brand/10 overflow-hidden"><div className="h-full w-1/3 bg-brand rounded-full animate-[gkslide_1.4s_ease-in-out_infinite]" /></div>
        </div>
      )}
      {isLoading || (busy && !data) ? <Skeleton /> : isError ? (
        <p className="text-sm text-red-600" data-testid="publisher-error">{formatApiError(error?.response?.data?.detail) || "Couldn't write captions — please try again."}</p>
      ) : data && (
        <div className="space-y-4">
          {PLATFORMS.filter((p) => data.captions[p.id]).map((p) => (
            <PlatformPost key={p.id} p={p} caption={data.captions[p.id]} link={data.links[p.id]} cardUrl={data.card_url} kind={kind} keyId={keyId} />
          ))}
        </div>
      )}
    </div>
  );
}

export default PublisherBody;
